/**
 * Same-site guardrails for live-URL embedding. A live URL is an arbitrary
 * third-party site rendered in an iframe; these helpers bound where we let it
 * navigate and — critically — which origin we post (PII-stripped) comment data
 * to, so a redirect can't drift the review off the intended site.
 *
 * NOTE: this is a no-dependency approximation of an eTLD+1 comparison. It
 * treats a host and any of its subdomains as same-site, which covers the real
 * cases (apex↔www, `app.`/locale subdomains, http→https). It intentionally
 * does NOT match different registrable domains (e.g. `example.com` vs
 * `example.de`). Its one blind spot is public-suffix hosts served as the live
 * URL itself (e.g. `foo.github.io` vs `bar.github.io` would wrongly match a
 * `github.io` anchor) — acceptable for now; swap in a public-suffix list if we
 * ever need true eTLD+1 accuracy.
 */

/** Parse a hostname (lowercased) from a URL, or `null` if it can't be parsed. */
export const getHostname = (url: string, base?: string): string | null => {
  try {
    return new URL(url, base).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * True when hosts `a` and `b` belong to the same site: identical, or one is a
 * subdomain of the other. The leading dot anchors the suffix match so
 * lookalikes don't pass — `notexample.com` ✗ `example.com`, and
 * `example.com.evil.com` ✗ `example.com`.
 */
export const isSameSiteHost = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => {
  if (!a || !b) return false;
  const hostA = a.toLowerCase();
  const hostB = b.toLowerCase();
  return (
    hostA === hostB ||
    hostA.endsWith(`.${hostB}`) ||
    hostB.endsWith(`.${hostA}`)
  );
};

/** Convenience: are two URLs (or a URL and a base) on the same site? */
export const isSameSiteUrl = (
  url: string,
  other: string,
  base?: string
): boolean => isSameSiteHost(getHostname(url, base), getHostname(other));
